import { cn } from "cn";
import type {
  Staff,
  OpeningHours,
  CalendarBlock,
} from "@/types/appointments/calendar-types";
import {
  TOTAL_ROWS,
  ROW_HEIGHT,
  DRAG_ACTIVATION_DISTANCE,
} from "@/constants/appointments/appointments-constants";
import {
  getTimeLabel,
  isOddHour,
  calculateInitialScrollPosition,
  rowIndexToTime,
  calculateResizedDurationRows,
  pixelsToRowDelta,
  clampRowIndex,
  rowsToMinutes,
} from "@/lib/appointments/calendar-utils";
import { StaffColumn } from "./staff-column";
import { DndContext, DragEndEvent, MouseSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useIntl } from "react-intl";

interface CalendarProps {
  staff: Staff[];
  openingHours: OpeningHours;
  appointments?: CalendarBlock[];
  onAppointmentUpdate: (
    lineId: number,
    appointmentId: number,
    newStaffId: string,
    newStartHour?: number,
    newStartMinute?: number,
    newDurationMinutes?: number
  ) => void;
  onAddAppointment: (staffId: string, rowIndex: number) => void;
  onEditAppointment: (appointmentId: number) => void;
  onStaffHeaderClick?: (staff: Staff) => void;
  readOnly?: boolean;
}

export function Calendar({
  staff,
  openingHours,
  appointments = [],
  onAppointmentUpdate,
  onAddAppointment,
  onEditAppointment,
  onStaffHeaderClick,
  readOnly = false,
}: CalendarProps) {
  const intl = useIntl();
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: DRAG_ACTIVATION_DISTANCE,
    },
  });

  const sensors = useSensors(mouseSensor);

  const handleDragEnd = (event: DragEndEvent) => {
    if (readOnly) return;
    const { active, over, delta } = event;

    if (!over) return;

    const dragType = active.data.current?.type as string;
    const lineId = active.data.current?.lineId as number;
    const appointmentId = active.data.current?.appointmentId as number;

    if (dragType === "resize") {
      const originalDurationRows = active.data.current?.durationRows as number;
      const originalDurationMinutes = active.data.current
        ?.durationMinutes as number;
      const startRow = active.data.current?.startRow as number;

      const newDurationRows = calculateResizedDurationRows(
        delta.y,
        originalDurationRows,
        startRow
      );

      const newDurationMinutes = rowsToMinutes(newDurationRows);

      if (newDurationMinutes !== originalDurationMinutes) {
        onAppointmentUpdate(
          lineId,
          appointmentId,
          active.data.current?.staffId as string,
          undefined,
          undefined,
          newDurationMinutes
        );
      }
      return;
    }

    const newStaffId = over.id as string;
    const currentStaffId = active.data.current?.staffId as string;
    const originalStartRow = active.data.current?.startRow as number;

    const rowDelta = pixelsToRowDelta(delta.y);
    const newStartRow = clampRowIndex(originalStartRow + rowDelta);

    const { hour: newStartHour, minute: newStartMinute } =
      rowIndexToTime(newStartRow);

    const staffChanged = newStaffId !== currentStaffId;
    const timeChanged = newStartRow !== originalStartRow;

    if (staffChanged || timeChanged) {
      onAppointmentUpdate(
        lineId,
        appointmentId,
        newStaffId,
        newStartHour,
        newStartMinute
      );
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col h-[calc(100vh-8rem)] border-t overflow-hidden">
        <div
          ref={(element) => {
            if (element && openingHours) {
              element.scrollTop = calculateInitialScrollPosition(
                openingHours,
                appointments
              );
            }
          }}
          className="flex-1 overflow-auto"
        >
          <div className="flex flex-col min-w-fit">
            <div className="flex border-b bg-secondary shrink-0 sticky top-0 z-50">
              <div className="w-11 shrink-0 border-r p-2 sticky left-0 bg-secondary z-50" />
              <div className="flex flex-1">
                {staff.map((staffMember) =>
                  readOnly ? (
                    <div
                      key={staffMember.id}
                      className="min-w-56 flex-1 border-r p-2 text-xs text-muted-foreground text-center font-semibold"
                    >
                      {staffMember.name}
                    </div>
                  ) : (
                    <button
                      key={staffMember.id}
                      type="button"
                      className="min-w-56 flex-1 border-0 border-r p-2 text-xs text-muted-foreground text-center font-semibold cursor-pointer bg-transparent"
                      onClick={() => onStaffHeaderClick?.(staffMember)}
                    >
                      {staffMember.name}
                    </button>
                  )
                )}
              </div>
            </div>

            <div className="flex">
              <div className="w-11 shrink-0 sticky left-0 bg-background z-40">
                {Array.from({ length: TOTAL_ROWS }).map((_, rowIndex) => {
                  const timeLabel = getTimeLabel(intl, rowIndex);
                  const isAlternate = isOddHour(rowIndex, openingHours);
                  const isHourBoundary = rowIndex % 4 === 3 && rowIndex > 0;

                  return (
                    <div
                      key={rowIndex}
                      className={cn(
                        "border-r flex items-center justify-center",
                        isAlternate ? "bg-secondary" : "bg-background",
                        isHourBoundary && "border-b"
                      )}
                      style={{ height: `${ROW_HEIGHT}px` }}
                    >
                      {timeLabel && (
                        <span className="text-xs text-muted-foreground font-semibold mt-3">
                          {timeLabel}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-1">
                {staff.map((staffMember) => (
                  <StaffColumn
                    key={staffMember.id}
                    staffId={staffMember.id}
                    openingHours={openingHours}
                    workingHours={staffMember.working_hours}
                    appointments={appointments.filter(
                      (block) => String(block.staff_id) === staffMember.id
                    )}
                    onAddAppointment={onAddAppointment}
                    onEditAppointment={onEditAppointment}
                    readOnly={readOnly}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DndContext>
  );
}
